import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  reviewableDocumentInputSchema,
} from '@bordrio/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CheckedInput, FormLabel } from '@src/components';
import { useUpdateNifOrderMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import ReviewableDocumentCategoryInput from '../../components/ReviewableDocumentCategoryInput/ReviewableDocumentCategoryInput';
import { extractReviewableDocumentInputFromNifOrder } from '../../utils/reviewable-document';
import { NifOrderStepPageProps } from '../types/stepProps';
import { verificationCheckboxSchema } from '../utils/validation';

const formSchema = z.object({
  proofOfAddress: reviewableDocumentInputSchema,
  verifications: z.object({
    stillValid: verificationCheckboxSchema,
    namesMatch: verificationCheckboxSchema,
    english: verificationCheckboxSchema,
  }),
});

type FormData = z.infer<typeof formSchema>;

function NifProofOfAddressPage({
  nifOrder,
  previousUrl,
  navigateToNextStep,
}: NifOrderStepPageProps): JSX.Element {
  const proofOfAddressDoc = extractReviewableDocumentInputFromNifOrder(
    nifOrder,
    'nif_proof_of_address',
    'driver_license',
  );

  const hasDocument = !!proofOfAddressDoc.currentVersion?.fileOne;

  const { handleSubmit, control, setValue } = useForm<FormData>({
    defaultValues: {
      proofOfAddress: proofOfAddressDoc,
      verifications: {
        stillValid: hasDocument || undefined,
        namesMatch: hasDocument || undefined,
        english: hasDocument || undefined,
      },
    },
    resolver: zodResolver(formSchema),
  });
  const { t } = useTranslation(['nif']);
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const toast = useToast();

  function onSubmit(formData: FormData): Promise<void> {
    return updateNifOrder({
      variables: {
        input: {
          id: nifOrder.id,
          data: {
            intakeDocuments: [
              {
                documentCategoryId:
                  REVIEWABLE_DOCUMENT_CATEGORIES.nif_proof_of_address.id,
                reviewableDocument: formData.proofOfAddress,
              },
            ],
          },
        },
      },
    })
      .then((updatedOrder) => {
        navigateToNextStep(updatedOrder?.data?.updateNifOrder?.nifOrder);
      })
      .catch((err) => toast.error(logAndFormatError(err)));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>{t('proof_of_address.title', 'Proof of address')}</h2>
        <p>
          {t(
            'proof_of_address.body',
            "The address on the document must match the residential address provided. If requesting a NIF on behalf of a minor, please use the proof of address of one of the minor's parents.",
          )}
        </p>
      </div>
      <ReviewableDocumentCategoryInput
        control={control}
        name="proofOfAddress"
        category="nif_proof_of_address"
        setValue={setValue}
      />
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <div className="space-y-4">
          <FormLabel>
            {t(
              'upload_checklists.instructions',
              'Please verify the following regarding your upload:',
            )}
          </FormLabel>
          <CheckedInput.LabelledController
            control={control}
            name="verifications.stillValid"
            label={t(
              'proof_of_address.checklist.valid_doc',
              'Document is valid (i.e. not expired)',
            )}
          />
          <CheckedInput.LabelledController
            control={control}
            name="verifications.namesMatch"
            label={t(
              'proof_of_address.checklist.names_match',
              'First name and surname match your proof of identity exactly',
            )}
          />
          <CheckedInput.LabelledController
            control={control}
            name="verifications.english"
            label={t(
              'proof_of_address.checklist.english',
              'The document is in English or includes an English translation',
            )}
          />
        </div>
      </div>

      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default NifProofOfAddressPage;
